import type { AccordionItemProps } from "@project/ui";
import {
    AccordionButton,
    AccordionItem,
    AccordionPanel as ChakraAccordionPanel,
    AspectRatio,
    Box,
    CldImage,
    Grid,
    GridItem,
    Heading,
    Link,
    Text,
    hasCloudinaryAsset,
    mediaQueryMinWidth,
    getImageIsLandscape,
    isCloudinaryVideo,
    CldVideoLite,
} from "@project/ui";
import { AccordionIcon } from "./accordion-icon";
import { LinkWrapper } from "@/components/link-wrapper";
import { richTextRenderer } from "@/lib/contentful/rich-text-renderer/rich-text-renderer";
import type {
    ModuleAccordionFieldsFragment,
    PartAccordionItemFieldsFragment,
} from "@/components/contentful/module-accordion/__generated/module-accordion.contentful.generated";
import { frostedGlassStyle } from "@porsche-design-system/components-react/styles";
// eslint-disable-next-line no-restricted-imports
import type { Theme } from "@porsche-design-system/components-react";
import { useRouter } from "next/router";
import { useAppStore } from "@/store/app-store";
import { PAGMSHEvents, sendPagDataToGTM } from "@/lib/google-tag-manager/events";
import type { MouseEvent } from "react";

type PartAccordionItemProps = Omit<AccordionItemProps, "title"> &
    PartAccordionItemFieldsFragment &
    Pick<ModuleAccordionFieldsFragment, "theme"> & {
        counter: number;
        moduleName?: string | null;
        modulePosition?: number | null;
    };

type AccordionHeaderProps = Pick<PartAccordionItemProps, "title" | "theme" | "__typename"> & {
    counter: number;
    onClick: (e: MouseEvent<HTMLButtonElement>) => void;
};

const count = (index: number) => (index < 9 ? `0${index + 1}` : index + 1);

const getColEnd = ({
    hasImage,
    imageIsLandscape,
}: {
    hasImage: boolean;
    imageIsLandscape: boolean;
}) => {
    let colEnd = 9;
    if (hasImage) {
        colEnd = imageIsLandscape ? 4 : 6;
    }
    return colEnd;
};

const AccordionHeader = ({ counter, title, theme, onClick }: AccordionHeaderProps) => {
    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
        onClick(e);
    };

    return (
        <Heading as="h3" size="headingMedium" w="100%" display="flex" alignItems="center" gap={3}>
            <AccordionButton
                borderBottomWidth="1px"
                borderColor={theme === "light" ? "grey600" : "grey200"}
                position="relative"
                _expanded={{
                    bg: theme === "light" ? "blackOpaque" : "backgroundShading",
                    ...frostedGlassStyle,
                    borderColor: theme === "light" ? "blackOpaque" : "backgroundShading",
                }}
                _hover={{
                    bg: theme === "light" ? "blackOpaque" : "backgroundShading",
                    ...frostedGlassStyle,
                }}
                px={5}
                py={5}
                onClick={handleClick}
            >
                <Text as="span" flex="1" textAlign="left">
                    {count(counter)} {title}
                </Text>
                <AccordionIcon theme={theme as Theme} />
            </AccordionButton>
        </Heading>
    );
};

const AccordionPanel = (props: PartAccordionItemProps) => {
    const { description, asset, assetCaption, theme, link, table, moduleName, modulePosition } =
        props;

    const imageIsLandscape = getImageIsLandscape(asset);

    const colEnd = getColEnd({ hasImage: !!asset, imageIsLandscape });

    return (
        <ChakraAccordionPanel>
            <Grid
                templateColumns={{
                    base: "repeat(1, 1fr)",
                    l: "repeat(8, 1fr)",
                }}
                gap={{ base: 5, l: 8 }}
                my={{ base: 6, l: 8 }}
                px={5}
            >
                <GridItem
                    rowStart={{ base: 1, l: 1 }}
                    colStart={{ base: 1, l: 1 }}
                    colEnd={{
                        base: 2,
                        l: colEnd,
                    }}
                >
                    <Text>{description ?? ""}</Text>

                    {link && (
                        <GridItem
                            colStart={{ base: 1, l: 1 }}
                            colEnd={{ base: 2, l: colEnd }}
                            pt={6}
                            display={{ base: "none", l: "block" }}
                        >
                            <LinkWrapper
                                theme={theme as Theme}
                                renderAs={Link}
                                renderExternalLinkAs={Link}
                                item={link}
                                sx={{
                                    w: "var(--sizes-full)",
                                    [mediaQueryMinWidth.md]: {
                                        w: "auto",
                                    },
                                }}
                                moduleName={moduleName}
                                modulePosition={modulePosition}
                                eventAction={PAGMSHEvents.accordionLinkClick}
                            />
                        </GridItem>
                    )}
                </GridItem>

                {hasCloudinaryAsset(asset) && (
                    <GridItem
                        rowStart={{ base: 2, l: 1 }}
                        colStart={{ base: 1, l: imageIsLandscape ? 4 : 6 }}
                        colEnd={{ base: 2, l: 9 }}
                    >
                        <Box as="figure" pos={"relative"}>
                            <AspectRatio ratio={[imageIsLandscape ? "16:9" : "9:16"]}>
                                {isCloudinaryVideo(asset) ? (
                                    <CldVideoLite
                                        cloudinaryAsset={asset}
                                        wrapperProps={{
                                            borderRadius: "large",
                                        }}
                                        watchIsInView={false}
                                    />
                                ) : (
                                    <CldImage cloudinaryAsset={asset} fill />
                                )}
                            </AspectRatio>
                        </Box>

                        {assetCaption && (
                            <Text size="xx-small" mt={{ base: 4, l: 6 }}>
                                {assetCaption}
                            </Text>
                        )}
                    </GridItem>
                )}

                {link && (
                    <GridItem
                        colStart={{ base: 1, l: 1 }}
                        colEnd={{ base: 2, l: colEnd }}
                        display={{ base: "block", l: "none" }}
                    >
                        <LinkWrapper
                            theme={theme as Theme}
                            renderAs={Link}
                            item={link}
                            sx={{
                                w: "var(--sizes-full)",
                                [mediaQueryMinWidth.md]: {
                                    w: "auto",
                                },
                            }}
                            moduleName={moduleName}
                            modulePosition={modulePosition}
                            eventAction={PAGMSHEvents.accordionLinkClick}
                        />
                    </GridItem>
                )}
            </Grid>

            {table && (
                <Box
                    className="rte"
                    w="full"
                    sx={{
                        ...(theme !== "light" && { "& th": { color: "allWhite" } }),
                    }}
                >
                    {richTextRenderer({
                        ...table,
                        theme: theme === "auto" ? "light" : (theme as "light" | "dark"),
                    })}
                </Box>
            )}
        </ChakraAccordionPanel>
    );
};

export const PartAccordionItem = (props: PartAccordionItemProps) => {
    const { title, counter, moduleName, modulePosition } = props;

    const { locale } = useRouter();
    const {
        state: { pageId, pageContentTags, pageType },
    } = useAppStore();

    const handleButtonClick = () => {
        const clickElementType = "interaction";
        const clickElementId = pageId;
        const clickElementName = `Accordion ${count(counter)} ${title}`;

        sendPagDataToGTM({
            eventAction: PAGMSHEvents.accordionToggleButtonClick,
            locale: locale!,
            pageExperience: {
                pageCategory: pageType,
                contentTags: pageContentTags ?? [],
            },
            context: {
                moduleName: moduleName,
                modulePosition: modulePosition,
            },
            componentClick: {
                clickElementType,
                clickElementId,
                clickElementName,
            },
        });
    };

    return (
        <AccordionItem>
            <AccordionHeader
                title={props.title}
                counter={props.counter}
                theme={props.theme}
                __typename={props.__typename}
                onClick={handleButtonClick}
            />
            <AccordionPanel {...props} />
        </AccordionItem>
    );
};
